import React, { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import Button from 'components/Shared/Common/Button';
import Icon from 'components/Shared/Common/Icon';
import { MultipleActions } from 'components/Shared/User/Item/Actions/actions';

import MemberItemList from 'components/Shared/User/MemberItemList';
import isEqual from 'react-fast-compare';
import CategoryTitle from 'components/Shared/Layout/CategoryTitle';
import FeedDrawer from '../FeedDrawer';

const formatMessages = intl => () => ({
    drawerTitle: intl.formatMessage({
        id: 'smallDrawer.feed.contacts.title',
        defaultMessage: 'Membres'
    }),
    fallbackMessage: intl.formatMessage({
        id: 'smallDrawer.feed.contacts.fallbackMessage',
        defaultMessage: 'Vous n\'avez invité personne dans ce groupe.'
    }),
    inviteContact: intl.formatMessage({
        id: 'smallDrawer.feed.contacts.button.tooltip',
        defaultMessage: 'Inviter des membres'
    })
});

const MemberDrawer = (props) => {
    const {
        intl,
        requestFeedMember,
        onClose,
        isMeFeedAdmin = false,
        openInviteContactDrawer,
        unlinkUser,
        _feed,
        _user,
        _acceptedMembers,
        _pendingMembers,
        _administratorsMembers,
        onChangeAdministrator
    } = props;
    const i18n = formatMessages(intl)();
    const countMembers = isMeFeedAdmin ? (_acceptedMembers?.length || 0) + (_pendingMembers?.length || 0) : (_acceptedMembers?.length || 0);
    const _members = isMeFeedAdmin ? [...(_acceptedMembers|| []), ...(_pendingMembers|| [])] : _acceptedMembers;
    const isLoading = false;
    const error = undefined;

    useEffect(() => {
        requestFeedMember();
    }, [_feed]);


    const handleKickUser = useMemo(() => _itemUser => () => {
        unlinkUser(_itemUser);
    }, [unlinkUser]);
    
    const handleChangeFeedAdministrator = useMemo(() => (type, _itemUser) => () => {
        onChangeAdministrator(type, _itemUser);
    }, [onChangeAdministrator]);
    
    const handleRenderMultipleActions = useMemo(() => ({ _user: _itemUser }) =>
        (_itemUser !== _user ? 
            <MultipleActions
                isAdmin={_administratorsMembers.includes(_itemUser)}
                isPendingMembers={_pendingMembers.includes(_itemUser)}
                onChangeFeedAdministrator={(type) => handleChangeFeedAdministrator(type, _itemUser)} 
                onKick={handleKickUser(_itemUser)} /> 
            : undefined
        ), [_user, _administratorsMembers, _pendingMembers, handleChangeFeedAdministrator, handleKickUser]);
        
    return (
        <FeedDrawer
            isOpen
            title={(
                <CategoryTitle
                    label={i18n.drawerTitle}
                    icon={<Icon size="xl" iconName="Users" />}
                    count={countMembers}
                />
            )}
            onClose={onClose}
            error={error}
            content={(
                <MemberItemList
                    _users={_members}
                    fallbackMessage={i18n.fallbackMessage}
                    actionRender={isMeFeedAdmin ? handleRenderMultipleActions : undefined}
                    _feed={_feed}
                    displayPendingBadge={isMeFeedAdmin}
                    isLoading={isLoading}
                />
            )}
            action={
                isMeFeedAdmin ? (
                    <Button
                        iconName="UserPlus"
                        color="primary"
                        onClick={openInviteContactDrawer}
                        tooltipPosition="bottom"
                        tooltipText={i18n.inviteContact}
                    />
                ) : null
            }
        />
    );

}

MemberDrawer.propTypes = {
    intl: PropTypes.any.isRequired,
    _feed: PropTypes.string,
    _user: PropTypes.string,
    _acceptedMembers: PropTypes.array,
    _pendingMembers: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    unlinkUser: PropTypes.func.isRequired,
    isMeFeedAdmin: PropTypes.bool,
    openInviteContactDrawer: PropTypes.func.isRequired,
    onChangeAdministrator: PropTypes.func.isRequired,
};

export default injectIntl(memo(MemberDrawer, isEqual));
